"use client";
import {
  CheckInCard,
  CheckInCardProps
} from "@gettactic/components/src/components/reservation-card/CheckInCard";
import { ExtendReservationDialog } from "@/features/reservation/components/ReservationCard/ExtendReservationDialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/api";
import { useDisclosure } from "@gettactic/helpers";
import { isRoom, isWorkspace } from "@gettactic/helpers/src/resources/utils";
import { useAuthenticated } from "@gettactic/hooks";
import { toast } from "react-hot-toast";
import { useState } from "react";

export type CheckInCardOnWebProps = Omit<
  CheckInCardProps,
  "onCheckin" | "onCheckout" | "loading" | "showCheckInButton"
>;

export const CheckInCardOnWeb = ({
  reservation,
  onEdit,
  onCancel,
  onDuplicate,
  ...restProps
}: CheckInCardOnWebProps) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const extendDisclosure = useDisclosure();
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();

  // Remote check-in is an office setting, per resource type. Check the
  // reservation's OWN office (agenda items can belong to any office) and
  // the setting matching the resource type — otherwise a desk reservation
  // would show Check in just because meeting rooms allow it (or vice
  // versa). Falls back to the current office when the reservation's
  // office isn't in the user's office list.
  const office =
    authenticatedUser?.offices?.byId[reservation.resource.office_id] ??
    authenticatedUser?.offices?.byId[currentOffice.id];
  const resourceType = reservation.resource.type;
  const showCheckInButton = isRoom(resourceType)
    ? !!office?.meeting_room_remote_checkin
    : isWorkspace(resourceType)
      ? !!office?.workspace_remote_checkin
      : // Types without their own setting (e.g. parking) keep the old
        // behavior: visible if any remote check-in is enabled.
        !!(
          office?.meeting_room_remote_checkin ||
          office?.workspace_remote_checkin
        );

  const checkinMutation = useMutation({
    mutationFn: async ({
      scheduleId,
      userId
    }: {
      scheduleId: string;
      userId: string;
    }) => {
      setLoading(true);
      await api.client.schedules.checkinUser(scheduleId, userId);

      queryClient.invalidateQueries({ queryKey: ["schedules"] });
      queryClient.invalidateQueries({ queryKey: ["myAgenda"] });
    },

    onSuccess: () => {
      setLoading(false);
      toast.success("You're checked in");
    },

    onError: () => {
      setLoading(false);
      toast.error(`Sorry we couldn't complete the check in`);
    }
  });

  const checkoutMutation = useMutation({
    mutationFn: async ({
      scheduleId,
      userId
    }: {
      scheduleId: string;
      userId: string;
    }) => {
      setLoading(true);
      await api.client.schedules.checkoutUser(scheduleId, userId);
      // Use proper query invalidation format for tanstack/react-query
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
      queryClient.invalidateQueries({ queryKey: ["myAgenda"] });
      queryClient.invalidateQueries({
        queryKey: ["tableOverview"]
      });
    },

    onSuccess: () => {
      setLoading(false);
      toast.success("You've checked out");
    },

    onError: () => {
      setLoading(false);
      toast.error(`Sorry we couldn't complete the checkout`);
    }
  });

  // Presence is per user, not per slot: one call covers every resource of the
  // reservation. The card still hands over the slot it acted on, so its user is what
  // identifies who is being checked in.
  const userOfSlot = (slotId: string) =>
    reservation.slots.find((slot) => slot.id === slotId)?.user.id;

  const handleCheckin = (
    _resourceId: string,
    scheduleId: string,
    slotId: string
  ) => {
    const userId = userOfSlot(slotId);
    if (!userId) return;
    checkinMutation.mutate({ scheduleId, userId });
  };

  const handleCheckout = (
    _resourceId: string,
    scheduleId: string,
    slotId: string
  ) => {
    const userId = userOfSlot(slotId);
    if (!userId) return;
    checkoutMutation.mutate({ scheduleId, userId });
  };

  return (
    <>
      <CheckInCard
        reservation={reservation}
        onCheckin={handleCheckin}
        onCheckout={handleCheckout}
        loading={loading}
        showCheckInButton={showCheckInButton}
        onDuplicate={onDuplicate}
        onExtend={extendDisclosure.open}
        onEdit={onEdit}
        onCancel={onCancel}
        {...restProps}
      />
      <ExtendReservationDialog
        isOpen={extendDisclosure.isOpen}
        onOpenChange={extendDisclosure.onOpenChange}
        onClose={extendDisclosure.close}
        schedule={reservation}
      />
    </>
  );
};
